import React, { useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { Input } from 'formik-antd';
import { FormikProps } from 'formik';
import { useDidUpdate } from 'rooks';

import FormItem from 'components/formik/FormItem';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import {
  countriesSelector,
  isCountriesLoadingSelector,
  countryCitiesSelector,
  isCountryCitiesLoadingSelector,
} from 'redux/selectors/regionSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import { WorkerInfoFormValues } from '../WorkerSection';

type Props<T> = {
  formik: FormikProps<T>;
  isAddWorkerOptionSelected: boolean;
  handleWorkerAddressChange: (value?: string) => void;
};

export default function InternationalWorkerStateForm<
  T extends WorkerInfoFormValues,
>(props: Props<T>) {
  const { formik, isAddWorkerOptionSelected, handleWorkerAddressChange } =
    props;
  const [selectedCountry, setSelectedCountry] = useState(
    formik.initialValues.worker.country,
  );
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const cities = useAppSelector(countryCitiesSelector);
  const isCountryCitiesLoading = useAppSelector(isCountryCitiesLoadingSelector);
  const filteredCities = useMemo(
    () => cities.filter((city) => city.countryId === selectedCountry),
    [selectedCountry, cities],
  );

  useDidUpdate(() => {
    setSelectedCountry(undefined);
  }, [formik.values.worker.workerId]);

  const handleCountryChange = (countryId: number, formik: FormikProps<T>) => {
    setSelectedCountry(countryId);

    formik.setFieldValue('worker.city', '', false);
    formik.setFieldValue('worker.state', '', false);
    formik.setFieldValue('worker.internationalCityName', '', false);
    formik.setFieldValue('worker.internationalStateName', '', false);
  };

  return (
    <Row gutter={40}>
      <Col xs={24} md={12}>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <FormItem
              name="worker.country"
              label="Country *"
              data-id="newAssignmentWorkerCountryField"
            >
              <FormikSelectWithLoading
                isLoading={isCountriesLoading}
                name="worker.country"
                showSearch
                placeholder="Select country"
                optionFilterProp="children"
                disabled={!isAddWorkerOptionSelected}
                onChange={(value: number) => handleCountryChange(value, formik)}
              >
                {countries.map((country) => (
                  <FormikSelectWithLoading.Option
                    key={`select-country-${country.id}`}
                    value={country.id || -1}
                  >
                    {country.name}
                  </FormikSelectWithLoading.Option>
                ))}
              </FormikSelectWithLoading>
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem
              name="worker.internationalStateName"
              label="State *"
              data-id="newAssignmentWorkerStateField"
            >
              <Input
                name="worker.internationalStateName"
                placeholder="Enter state"
                disabled={
                  !formik.values.worker.country || !isAddWorkerOptionSelected
                }
                autoComplete="off"
              />
            </FormItem>
          </Col>
        </Row>
      </Col>

      <Col xs={24} md={12}>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <FormItem
              data-id="workerCityField"
              name="worker.internationalCityName"
              label="City *"
            >
              <FormikAutoCompleteWithLoading
                isLoading={isCountryCitiesLoading}
                name="worker.internationalCityName"
                filterOption
                disabled={
                  !formik.values.worker.internationalStateName ||
                  !isAddWorkerOptionSelected
                }
                placeholder="Enter city"
                showSearch
              >
                {filteredCities.map((city) => (
                  <FormikAutoCompleteWithLoading.Option
                    key={`city-state-${city.id}`}
                    value={city.name || ''}
                  >
                    {city.name}
                  </FormikAutoCompleteWithLoading.Option>
                ))}
              </FormikAutoCompleteWithLoading>
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem
              label="Address *"
              data-id="newAssignmentWorkerAddressField"
              name="worker.address"
            >
              <Input
                disabled={!isAddWorkerOptionSelected}
                name="worker.address"
                placeholder="Enter address"
                autoComplete="off"
                onChange={(e) => handleWorkerAddressChange(e.target.value)}
                suffix={<span />}
              />
            </FormItem>
          </Col>
        </Row>
      </Col>

      <Col xs={24} md={12}>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <FormItem
              label="ZIP"
              data-id="newAssignmentWorkerZipField"
              name="worker.zip"
            >
              <Input
                disabled={!isAddWorkerOptionSelected}
                name="worker.zip"
                placeholder="Enter ZIP code"
                autoComplete="off"
              />
            </FormItem>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
